import { useTranslation } from 'react-i18next'

import {
  useClearNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../queries'
import { renderNotification } from '../render'
import type { Notification } from '../types'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { cn } from '@/shared/lib/utils'

export function NotificationList({ workspaceId }: { workspaceId: number }) {
  const { t } = useTranslation()
  const { data, isPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useNotifications(workspaceId)
  const markAllRead = useMarkAllNotificationsRead()
  const clearAll = useClearNotifications()

  if (isPending) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  const notifications = data?.pages.flatMap((page) => page.results) ?? []

  if (notifications.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        {t('notification.empty')}
      </p>
    )
  }

  return (
    <div className="flex max-h-96 flex-col">
      <div className="flex gap-1 border-b px-2 py-1">
        <Button
          variant="ghost"
          size="sm"
          className="text-xs"
          disabled={markAllRead.isPending}
          onClick={() => markAllRead.mutate(workspaceId)}
        >
          {t('notification.markAllRead')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs"
          disabled={clearAll.isPending}
          onClick={() => clearAll.mutate(workspaceId)}
        >
          {t('notification.clearAll')}
        </Button>
      </div>

      <ul className="overflow-y-auto">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            workspaceId={workspaceId}
            notification={notification}
          />
        ))}
      </ul>

      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          className="border-t text-xs"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {t('notification.more')}
        </Button>
      )}
    </div>
  )
}

function NotificationItem({
  workspaceId,
  notification,
}: {
  workspaceId: number
  notification: Notification
}) {
  const { t } = useTranslation()
  const markRead = useMarkNotificationRead()
  const content = renderNotification(notification)
  const unread = notification.read !== true

  return (
    <li>
      {/*
        A button rather than a link. Several of these types point at a row, and
        the row modal does not exist until M6; marking read is the whole of what
        this can honestly do today, and a link to nowhere would be worse than a
        control that does the one useful thing.
      */}
      <button
        type="button"
        className={cn(
          'w-full px-4 py-3 text-left transition-colors hover:bg-accent',
          unread && 'bg-accent/40',
        )}
        aria-label={unread ? t('notification.markRead') : undefined}
        disabled={!unread}
        onClick={() => {
          markRead.mutate({ workspaceId, notificationId: notification.id })
        }}
      >
        <span className="flex items-start gap-2">
          {unread && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />}
          <span className="min-w-0 flex-1">
            <span className="block text-sm">{t(content.key, content.values)}</span>
            {content.body !== undefined && content.body !== '' && (
              <span className="mt-1 block truncate text-xs whitespace-pre-line text-muted-foreground">
                {content.body}
              </span>
            )}
          </span>
        </span>
      </button>
    </li>
  )
}
